/* The Organic Butchery — component-product.js
   PDP island: variant selection, gallery, despatch countdown,
   sticky mobile ATC bar, lazy product recommendations.
   Loaded (deferred) only on product pages. */

const form = document.querySelector('[data-pdp-form]');
const jsonEl = document.querySelector('[data-product-json]');
const productData = jsonEl ? JSON.parse(jsonEl.textContent) : null;

/* ---------- variant selection ---------- */

function selectedOptions() {
  const opts = [];
  /* the variant picker block lives outside the form — query the page */
  document.querySelectorAll('.pdp__option input[type="radio"][name^="option-"]:checked')
    .forEach((input) => { opts[parseInt(input.name.split('-')[1], 10) - 1] = input.value; });
  return opts;
}

function findVariant(opts) {
  return productData.variants.find((v) => v.options.every((o, i) => o === opts[i]));
}

let currentVariant = null;

/* Neutralize Appstle's PDP widget without ripping it out of the DOM.
   CSS already hides it; we only disable fields so a hidden radio cannot
   submit a selling_plan. No MutationObserver — Appstle reinjects and that
   fight is expensive. */
function neutralizeAppstlePdp() {
  if (!form) return;
  form.querySelectorAll('.appstle_sub_widget, .appstle-tootltip-parent').forEach((el) => {
    el.setAttribute('hidden', '');
    el.setAttribute('aria-hidden', 'true');
  });
  form.querySelectorAll('.appstle_sub_widget input, .appstle_sub_widget select').forEach((el) => {
    el.disabled = true;
    if (el.name === 'selling_plan') el.removeAttribute('name');
  });
}

function selectedPlan() {
  if (!currentVariant || !currentVariant.plans) return null;
  // Appstle renders its widget outside the <form>, so search the whole buy
  // column, not just the form, for the currently-selected selling plan.
  const scope = (form && form.closest('.pdp__buy')) || document;
  const el = scope.querySelector('input[name="selling_plan"]:checked')
    || scope.querySelector('[name="selling_plan"]');
  const id = el && !el.disabled ? el.value : '';
  return id ? currentVariant.plans[id] || null : null;
}

function renderPrice() {
  if (!currentVariant) return;
  const plan = selectedPlan();
  const priceText = plan ? plan.price : currentVariant.price;
  const unitText = plan ? plan.unit : currentVariant.unit;
  const price = document.querySelector('[data-price]');
  const unit = document.querySelector('[data-unit-price]');
  const compare = document.querySelector('[data-compare-price]');
  const save = document.querySelector('[data-save-percent]');
  const atcPrice = form.querySelector('[data-atc-price]');
  const barPrice = document.querySelector('[data-buybar-price]');
  if (price) price.textContent = priceText;
  if (unit) unit.textContent = unitText;
  if (atcPrice) atcPrice.textContent = priceText;
  if (barPrice) barPrice.textContent = priceText;

  /* Compare + save %: one-time shows the variant's sale; a subscription shows
     its own discount vs the one-time price, so the row never goes half-empty. */
  let compareText = '';
  let saveNum = null;
  if (plan) {
    if (plan.save != null && plan.save > 0) {
      compareText = plan.compare || currentVariant.price;
      saveNum = plan.save;
    }
  } else if (currentVariant.compare && currentVariant.save) {
    compareText = currentVariant.compare;
    saveNum = currentVariant.save;
  }
  if (compare) {
    compare.textContent = compareText;
    compare.hidden = !compareText;
  }
  if (save) {
    save.textContent = saveNum != null
      ? (window.strings.save_percent || 'Save __N__%').replace('__N__', String(saveNum))
      : '';
    save.hidden = saveNum == null;
  }
}

function renderVariant(variant) {
  const idInput = form.querySelector('[data-variant-id]');
  const atc = form.querySelector('[data-atc]');
  const atcLabel = form.querySelector('[data-atc-label]');
  const barSubmit = document.querySelector('[data-buybar-submit]');

  if (!variant) {
    if (atc) atc.disabled = true;
    if (atcLabel) atcLabel.textContent = window.strings.unavailable;
    if (barSubmit) barSubmit.disabled = true;
    return;
  }
  currentVariant = variant;
  if (idInput) idInput.value = variant.id;
  renderPrice();
  if (atc) atc.disabled = !variant.available;
  if (barSubmit) barSubmit.disabled = !variant.available;
  if (atcLabel) atcLabel.textContent = variant.available ? window.strings.add_to_basket : window.strings.sold_out;

  const url = new URL(window.location);
  url.searchParams.set('variant', variant.id);
  window.history.replaceState({}, '', url);
}

if (form && productData) {
  const idInput = form.querySelector('[data-variant-id]');
  currentVariant = productData.variants.find((v) => String(v.id) === idInput.value) || productData.variants[0];

  /* One-shot + submit safety net. Idle fallback covers late Appstle inject. */
  neutralizeAppstlePdp();
  form.addEventListener('submit', neutralizeAppstlePdp, true);
  document.addEventListener(
    'AppstleSubscription:SubscriptionWidget:widgetInitialised',
    neutralizeAppstlePdp
  );
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1));
  idle(() => neutralizeAppstlePdp());

  /* option pills sit outside the form (movable block), so listen at document level */
  document.addEventListener('change', (e) => {
    if (e.target.matches('.pdp__option input[name^="option-"]')) {
      renderVariant(findVariant(selectedOptions()));
    }
    if (e.target.matches('[data-plan-toggle]')) {
      const planInput = form.querySelector('[data-selling-plan]');
      if (planInput) {
        planInput.value = e.target.value;
        planInput.disabled = e.target.value === '';
      }
      renderPrice();
    }
    // Appstle's Subscribe & Save (radios) or its frequency dropdown → follow it
    if (e.target.name === 'selling_plan' || (e.target.closest && e.target.closest('.appstle_sub_widget'))) {
      renderPrice();
    }
  });

  /* qty stepper (outside the cart drawer, so theme.js ignores it) */
  form.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const input = form.querySelector('input[data-qty-value]');
    const next = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.qtyChange, 10));
    input.value = next;
  });
}

/* ---------- gallery thumb swap ---------- */

const gallery = document.querySelector('[data-gallery]');
if (gallery) {
  gallery.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-gallery-thumb]');
    if (!thumb) return;
    const main = gallery.querySelector('[data-gallery-main]');
    if (!main) return;
    main.src = thumb.dataset.full;
    main.srcset = thumb.dataset.srcset;
    gallery.querySelectorAll('[data-gallery-thumb]').forEach((t) => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
  });
}

/* ---------- despatch countdown ---------- */

const despatch = document.querySelector('[data-despatch]');
if (despatch) {
  const cutoff = parseInt(despatch.dataset.cutoff, 10);
  const timeEl = despatch.querySelector('[data-despatch-time]');
  const tick = () => {
    const now = new Date();
    const target = new Date(now);
    target.setHours(cutoff, 0, 0, 0);
    if (now >= target) target.setDate(target.getDate() + 1);
    const mins = Math.floor((target - now) / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    timeEl.textContent = h > 0 ? `${h} hr${h === 1 ? '' : 's'} ${m} min` : `${m} min`;
    despatch.hidden = false;
  };
  tick();
  setInterval(tick, 60000);
}

/* ---------- sticky mobile ATC bar ----------
   Shows ONLY once the main Add-to-Cart has been scrolled past — i.e. it has
   left the viewport off the TOP. Never while it is on screen, and never before
   the shopper has reached it (on phones the gallery comes first, so the button
   starts below the fold; "not intersecting" alone used to show the bar there).
   Scrolling back up to the button hides it again. */

const buybar = document.querySelector('[data-buybar]');
if (buybar && form) {
  const atcBlock = form.querySelector('.pdp__atc');
  buybar.querySelector('[data-buybar-submit]').addEventListener('click', () => form.requestSubmit());
  if ('IntersectionObserver' in window && atcBlock) {
    new IntersectionObserver(([entry]) => {
      const top = entry.rootBounds ? entry.rootBounds.top : 0;
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= top;
      buybar.hidden = !scrolledPast;
    }, { rootMargin: '-60px 0px 0px 0px' }).observe(atcBlock);
  }
  /* no IntersectionObserver: the bar stays hidden rather than risk showing above the button */
}

/* lazy product recommendations are handled globally in theme.js ([data-related]) */
